import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl'
import { Button, Row, Checkbox, Divider, Tooltip } from 'antd'
import { DownOutlined, DragOutlined } from '@ant-design/icons'
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'
import { isEqual } from 'lodash'

const btnStyle = {
  alignItems: 'center',
  display: 'flex',
  justifyContent: 'space-between',
  border: 'none',
}

const menuStyle = {
  background: 'white',
  padding: '8px',
  position: 'absolute',
  top: 48,
  zIndex: 9,
}

const areColumnsSame = (columns1, columns2) => {
  const keys1 = columns1.map(c => c.key).sort()
  const keys2 = columns2.map(c => c.key).sort()
  return isEqual(keys1, keys2)
}

/**
 * Changes visible property and order of table columns
 */
const ConfigDropdown = ({ tableName, columns, setColumns, loading }) => {

  const [open, setOpen] = useState(false)
  const [initialColumns, setInitialColumns] = useState()

  /* preserving saved column order */
  const mapToStored = storedColumns => storedColumns.map(storedCol => ({
    ...columns.find(col => col.key === storedCol.key),
    visible: storedCol.visible,
  }))

  /* not preserving saved column order */
  const mapToCurrent = storedColumns => columns.map(col => {
    const storedCol = storedColumns.find(stored => stored.key === col.key)
    return {
      ...col,
      visible: storedCol ? storedCol.visible : col.visible,
    }
  })

  const mapColumns = storedColumns => areColumnsSame(columns, storedColumns)
    ? mapToStored(storedColumns)
    : mapToCurrent(storedColumns)

  const currentColumns = localStorage.getItem(tableName)
    ? mapColumns(JSON.parse(localStorage.getItem(tableName)))
    : columns

  const closeOnClickOutside = e => {
    if (e.target.tagName === 'DIV') setOpen(false)
  }

  useEffect(() => {
    setColumns(currentColumns) // set valid columns on load
    setInitialColumns(columns)
    document.addEventListener('click', closeOnClickOutside)

    return () => window.removeEventListener('click', closeOnClickOutside)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const saveColumns = newColumns => {
    setColumns(newColumns)
    const mapped = newColumns.map(({ key, visible }) => ({ key, visible }))
    localStorage.setItem(tableName, JSON.stringify(mapped))
  }

  const changeVisible = ({ target }) => {
    const newColumns = currentColumns.map(col => target.name === col.key
      ? { ...col, visible: target.checked }
      : col
    )
    saveColumns(newColumns)
  }

  const changeVisibleAll = ({ target }) => {
    const newColumns = currentColumns.map(col => ({ ...col, visible: target.checked }))
    saveColumns(newColumns)
  }

  const reset = () => {
    localStorage.removeItem(tableName)
    setColumns(initialColumns)
  }

  const onDragEnd = ({ source, destination }) => {
    if (!destination) return // if outside list

    const newColumns = [...currentColumns]
    const [element] = newColumns.splice(source.index, 1)
    newColumns.splice(destination.index, 0, element)
    saveColumns(newColumns)
  }

  const draggableColumns = currentColumns.map(({ key, title, visible }, index) => (
    <Draggable key={key} draggableId={key} index={index}>
      {provided => (
        <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
          <Row type="flex" justify="space-between">
            <Checkbox
              name={key}
              checked={visible}
              onChange={changeVisible}
            >
              {title}
            </Checkbox>
            <DragOutlined />
          </Row>
        </div>
      )}
    </Draggable>
  ))

  const menu = (
    <div style={menuStyle}>
      <DragDropContext onDragEnd={onDragEnd}>
        <FormattedMessage id="Config.ChooseColumns" />
        <Divider style={{ margin: '4px 0px 4px 0px' }} />
        <Checkbox
          onChange={changeVisibleAll}
          checked={currentColumns.every(col => col.visible)}
        >
          <FormattedMessage id="Config.SelectAll" />
        </Checkbox>
        <Divider style={{ margin: '4px 0px 4px 0px' }} />
        <Droppable droppableId="droppable">
          {provided => (
            <div ref={provided.innerRef} {...provided.droppableProps}>
              {draggableColumns}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
        <Tooltip placement="bottom" title={<FormattedMessage id="Config.ResetToDefaults" />}>
          <Button onClick={reset} style={{ marginTop: 4 }}>
            <FormattedMessage id="Config.Reset" />
          </Button>
        </Tooltip>
      </DragDropContext>
    </div>
  )

  return (
    <>
      <Button
        style={btnStyle}
        loading={loading}
        onClick={() => setOpen(!open)}
      >
        <FormattedMessage id="Config.Button" />
        <DownOutlined />
      </Button>
      {open && menu}
    </>
  )
}

export default ConfigDropdown
